import React from "react";
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Tooltip,
  Chip,
  useTheme,
} from "@mui/material";
import { alpha, type Theme } from "@mui/material/styles";
import { ColorsEnum } from "../../../common/theme/colors";
import * as S from "../style";

interface ServiceStatus {
  name: string;
  url: string;
  ip_address?: string;
  status: string;
  tag?: string;
  gpu_enabled?: boolean;
  http_code?: number | null;
  latency_ms?: number | null;
  last_checked: string;
  error?: string | null;
}

interface ServiceStatusTableProps {
  services: ServiceStatus[];
  loading: boolean;
}

const getTagColor = (tag: string | undefined, theme: Theme) => {
  const d = theme.palette.mode === 'dark';
  switch (tag) {
    case "Frontend":
      return { bgcolor: alpha(theme.palette.info.main, d ? 0.15 : 0.12), color: theme.palette.info.main };
    case "Inference":
      return { bgcolor: alpha(theme.palette.secondary.main, d ? 0.15 : 0.12), color: d ? theme.palette.secondary.light : theme.palette.secondary.dark };
    case "Monitoring":
      return { bgcolor: alpha(theme.palette.warning.main, d ? 0.15 : 0.12), color: theme.palette.warning.main };
    case "Backend Server":
      return { bgcolor: alpha(theme.palette.success.main, d ? 0.15 : 0.12), color: theme.palette.success.main };
    default:
      return { bgcolor: theme.palette.action.selected, color: theme.palette.text.secondary };
  }
};

const getGpuChipColor = (gpuEnabled: boolean | undefined, theme: Theme) => {
  const d = theme.palette.mode === 'dark';
  if (gpuEnabled === true) {
    return { bgcolor: alpha(theme.palette.success.main, d ? 0.15 : 0.12), color: theme.palette.success.main };
  } else if (gpuEnabled === false) {
    return { bgcolor: alpha(theme.palette.error.main, d ? 0.15 : 0.12), color: theme.palette.error.main };
  }
  return { bgcolor: theme.palette.action.selected, color: theme.palette.text.secondary };
};

const ServiceStatusTable: React.FC<ServiceStatusTableProps> = ({
  services,
  loading,
}) => {
  const theme = useTheme();
  const oneLineCellSx = {
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  } as const;

  const handleRowClick = (url: string) => {
    window.open(url, "_blank");
  };

  return (
    <TableContainer component={S.StyledTableContainer}>
      <Table size="small" sx={{ tableLayout: "fixed" }}>
        <TableHead>
          <TableRow>
            <TableCell sx={{ width: "15%", ...oneLineCellSx }}>Service</TableCell>
            <TableCell sx={{ width: "24%", ...oneLineCellSx }}>URL</TableCell>
            <TableCell sx={{ width: "15%", ...oneLineCellSx }}>IP Address</TableCell>
            <TableCell sx={{ width: "8%", ...oneLineCellSx }}>Tag</TableCell>
            <TableCell sx={{ width: "6%", ...oneLineCellSx }}>GPU</TableCell>
            <TableCell sx={{ width: "5%", ...oneLineCellSx }}>Status</TableCell>
            <TableCell sx={{ width: "10%", ...oneLineCellSx }}>HTTP Code</TableCell>
            <TableCell sx={{ width: "7%", ...oneLineCellSx }}>Latency</TableCell>
            <TableCell sx={{ width: "10%", ...oneLineCellSx }}>Error</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {services.length === 0 ? (
            <TableRow>
              <TableCell colSpan={10} align="center" sx={{ py: 4 }}>
                <Typography color="text.secondary">
                  {loading ? "Loading..." : "No services configured"}
                </Typography>
              </TableCell>
            </TableRow>
          ) : (
            services.map((svc) => (
              <TableRow
                key={svc.name + svc.url}
                onClick={() => handleRowClick(svc.url)}
                sx={{
                  cursor: "pointer",
                  "&:hover": {
                    bgcolor: theme.palette.action.hover,
                  },
                  transition: "background-color 0.2s ease",
                }}
              >
              <TableCell sx={oneLineCellSx}>
                <Tooltip title={svc.name} arrow>
                  <Typography
                    variant="body2"
                    fontWeight={600}
                    sx={{
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      maxWidth: '100%',
                    }}
                  >
                    {svc.name}
                  </Typography>
                </Tooltip>
              </TableCell>
              <TableCell sx={oneLineCellSx}>
                <Tooltip title={svc.url} arrow>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{
                      fontFamily: 'monospace',
                      fontSize: '0.8rem',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      maxWidth: '100%',
                    }}
                  >
                    {svc.url}
                  </Typography>
                </Tooltip>
              </TableCell>
              <TableCell sx={oneLineCellSx}>
                <Tooltip title={svc.ip_address || '—'} arrow>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{
                      fontFamily: 'monospace',
                      fontSize: '0.8rem',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      maxWidth: '100%',
                    }}
                  >
                    {svc.ip_address || '—'}
                  </Typography>
                </Tooltip>
              </TableCell>
              <TableCell sx={oneLineCellSx}>
                {svc.tag ? (
                  <Chip
                    label={svc.tag}
                    size="small"
                    sx={{
                      ...getTagColor(svc.tag, theme),
                      fontWeight: 600,
                      fontSize: "0.7rem",
                      height: 20,
                    }}
                  />
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    —
                  </Typography>
                )}
              </TableCell>
              <TableCell sx={oneLineCellSx}>
                {svc.gpu_enabled !== undefined && svc.gpu_enabled !== null ? (
                  <Chip
                    label={svc.gpu_enabled ? "GPU" : "CPU"}
                    size="small"
                    sx={{
                      ...getGpuChipColor(svc.gpu_enabled, theme),
                      fontWeight: 600,
                      fontSize: "0.7rem",
                      height: 20,
                    }}
                  />
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    —
                  </Typography>
                )}
              </TableCell>
              <TableCell sx={oneLineCellSx}>
                <Box sx={{ ...S.StyledStatusDot, bgcolor: svc.status === "up" ? ColorsEnum.green : ColorsEnum.error }} />
              </TableCell>
              <TableCell sx={oneLineCellSx}>
                <Typography variant="body2" color="text.secondary" sx={oneLineCellSx}>
                  {svc.http_code ?? "—"}
                </Typography>
              </TableCell>
              <TableCell sx={oneLineCellSx}>
                <Typography variant="body2" color="text.secondary" sx={oneLineCellSx}>
                  {svc.latency_ms != null ? `${svc.latency_ms} ms` : "—"}
                </Typography>
              </TableCell>
              <TableCell sx={oneLineCellSx}>
                {svc.error ? (
                  <Tooltip title={svc.error} arrow>
                    <Typography
                      variant="body2"
                      color="error"
                      sx={{
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {svc.error}
                    </Typography>
                  </Tooltip>
                ) : (
                  <Typography variant="body2" color="text.secondary" sx={oneLineCellSx}>
                    —
                  </Typography>
                )}
              </TableCell>
            </TableRow>
          ))
        )}
      </TableBody>
    </Table>
  </TableContainer>
  );
};

export default ServiceStatusTable;
